import moment from 'moment';
import { DATE_FORMAT } from '../constants';
import { sortByDate } from './tableUtils';
import { reasons, agencies } from '../staticData';

const topNRecordsCount = 5;
const noOfDaysInAWeek = 7;
const noOfAWeeksInAMonth = 4;
const noOfMonthsInASixMonthSpan = 6;

export const asnTransitions = [
  'Planned',
  'Dropped',
  'LineOut',
  'LineOn',
  'RollOut',
  'WTCL',
  'SFLT',
];

export const getTotalLineOuts = data => {
  return sumValues(data);
};

export const getTotalEmergencyAsn = data => {
  return sumValues(data);
};

export const getTotalPlanRevisions = data => {
  return sumValues(data);
};

export const getTotalArchivedAsn = data => {
  return sumValues(data);
};

export const getTotalSequenceAdherence = data => {
  const arrayOfTotalPlannedCount = data.map(record => record.totalPlannedCount);
  const arrayOfTotalLineOutCount = data.map(record => record.linedOutCount);
  const totalPlannedCount = arrayOfTotalPlannedCount.reduce((a, b) => a + b, 0);
  const totalLineOutCount = arrayOfTotalLineOutCount.reduce((a, b) => a + b, 0);
  const totalPercentageForGivenDateRange =
    ((totalPlannedCount - totalLineOutCount) / totalPlannedCount) * 100;
  const sequenceAdherencePercentage =
    totalPlannedCount === 0 ? '--' : totalPercentageForGivenDateRange || 0;
  return sequenceAdherencePercentage;
};

export const getTotalPlannedAsnCount = data => {
  let totalPlannedAsn = 0;
  for (const record of data) {
    totalPlannedAsn += parseInt(record['plannedAsnCount']);
  }
  return totalPlannedAsn;
};

export const sumValues = data => {
  let totalLineOuts = 0;
  for (const record of data) {
    totalLineOuts += parseInt(record['value']);
  }
  return totalLineOuts;
};

export const getDayWiseLineOutReasonsGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(data, from, to, 'reason', reasons, 'timestamp');
};

export const getDayWiseLineOutAgenciesGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(
    data,
    from,
    to,
    'agency',
    agencies,
    'timestamp',
  );
};

export const getWeekWiseLineOutReasonsGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(
    data,
    from,
    to,
    'reason',
    reasons,
    'timestamp',
  );
};

export const getWeekWiseLineOutAgenciesGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(
    data,
    from,
    to,
    'agency',
    agencies,
    'timestamp',
  );
};

export const getDayWiseEmergencyAsnReasonsGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(data, from, to, 'reason', reasons, 'timestamp');
};

export const getDayWiseEmergencyAsnAgenciesGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(
    data,
    from,
    to,
    'agency',
    agencies,
    'timestamp',
  );
};

export const getWeekWiseEmergencyAsnReasonsGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(
    data,
    from,
    to,
    'reason',
    reasons,
    'timestamp',
  );
};

export const getWeekWiseEmergencyAsnAgenciesGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(
    data,
    from,
    to,
    'agency',
    agencies,
    'timestamp',
  );
};

export const getDayWiseArchivedAsnReasonsGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(data, from, to, 'reason', reasons, 'timestamp');
};

export const getDayWiseArchivedAsnAgenciesGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(
    data,
    from,
    to,
    'agency',
    agencies,
    'timestamp',
  );
};

export const getWeekWiseArchivedAsnReasonsGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(
    data,
    from,
    to,
    'reason',
    reasons,
    'timestamp',
  );
};

export const getWeekWiseArchivedAsnAgenciesGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(
    data,
    from,
    to,
    'agency',
    agencies,
    'timestamp',
  );
};

export const getDayWisePlanRevisionReasonsGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(data, from, to, 'reason', reasons, 'date');
};

export const getDayWisePlanRevisionAgenciesGraphData = (data, from, to) => {
  return getDayWiseGraphDataFor(data, from, to, 'agency', agencies, 'date');
};

export const getWeekWisePlanRevisionReasonsGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(data, from, to, 'reason', reasons, 'date');
};

export const getWeekWisePlanRevisionAgenciesGraphData = (data, from, to) => {
  return getWeekWiseGraphDataFor(data, from, to, 'agency', agencies, 'date');
};

const getDayWiseGraphDataFor = (
  data,
  from,
  to,
  contributingFactor,
  factorTypes,
  timestampField,
) => {
  const topNRecordsInAscendingOrder = getTopNRecordsCountMap(
    data,
    contributingFactor,
    factorTypes,
  );

  let reasonOrAgencyWithDateWiseCountMap = new Map();
  topNRecordsInAscendingOrder.forEach((value, key) => {
    let dateWiseRecordMap = new Map();
    for (let iterator = 0; iterator < noOfDaysInAWeek; iterator++) {
      let dateString = moment(from).add(iterator, 'd').format('DD MMM');
      dateWiseRecordMap.set(dateString, 0);
    }
    reasonOrAgencyWithDateWiseCountMap.set(key, dateWiseRecordMap);
  });

  let record;
  let reasonOrAgency;
  for (record of data) {
    reasonOrAgency = getTagValueForKey(record['tags'], contributingFactor);

    if (topNRecordsInAscendingOrder.has(reasonOrAgency)) {
      const timestamp = moment(record[timestampField]).format('DD MMM');
      const previousCount = reasonOrAgencyWithDateWiseCountMap
        .get(reasonOrAgency)
        .get(timestamp);
      const newCount = previousCount + parseInt(record['value']);
      reasonOrAgencyWithDateWiseCountMap.set(
        reasonOrAgency,
        reasonOrAgencyWithDateWiseCountMap
          .get(reasonOrAgency)
          .set(timestamp, newCount),
      );
    }
  }

  const maxRecordCount = calculateGraphMaximumRecordCount(
    reasonOrAgencyWithDateWiseCountMap,
  );
  const graphDataArray = buildD3SupportedMultiLineGraphData(
    reasonOrAgencyWithDateWiseCountMap,
  );
  const filteredGraphDataArray = getValidGraphData(graphDataArray);
  return [maxRecordCount, filteredGraphDataArray];
};

const getDateWiseRecordMap = data => {
  let dateWiseRecordMap = new Map();
  const sortedData = sortByDate(data);
  sortedData.forEach(record => {
    const sequenceAdherenceOfTheDay =
      ((record.totalPlannedCount - record.linedOutCount) /
        record.totalPlannedCount) *
      100;
    const sequenceAdherenceOfTheDayRoundUpToTwoDecimalPoints =
      Math.round(sequenceAdherenceOfTheDay * 100) / 100;
    dateWiseRecordMap.set(
      moment(record.date).format('DD MMM'),
      record.totalPlannedCount <= 0
        ? null
        : sequenceAdherenceOfTheDayRoundUpToTwoDecimalPoints > 0
          ? sequenceAdherenceOfTheDayRoundUpToTwoDecimalPoints
          : 0,
    );
  });
  return dateWiseRecordMap;
};


export const getDayWiseSequenceAdherenceGraphData = data => {
  let sequenceAdherenceMapData = new Map();
  data.forEach(item => {
    sequenceAdherenceMapData.set(
      item?.line?.label,
      getDateWiseRecordMap(item?.data),
    );
  });
  return buildD3SupportedMultiLineGraphData(sequenceAdherenceMapData);
};

const getWeekWiseCountMap = (data, weekDateRangeArray) => {
  let weekWiseCountMap = new Map();

  weekDateRangeArray.forEach(range => {
    const array = data.filter(
      record =>
        moment(record.date).isBetween(moment(range[0]), moment(range[1])) ||
        moment(record.date).isSame(moment(range[0])) ||
        moment(record.date).isSame(moment(range[1])),
    );
    const arrayOfWeeklyTotalPlannedCount = array.map(
      record => record.totalPlannedCount,
    );
    const arrayOfWeeklyLineOutCount = array.map(record => record.linedOutCount);
    const weeklyPlannedCount = arrayOfWeeklyTotalPlannedCount.reduce(
      (a, b) => a + b,
      0,
    );
    const weeklyLineOutCount = arrayOfWeeklyLineOutCount.reduce(
      (a, b) => a + b,
      0,
    );

    const sequenceAdherenceOfWeek =
      ((weeklyPlannedCount - weeklyLineOutCount) / weeklyPlannedCount) * 100 ||
      0;
    const sequenceAdherenceOfWeekRoundUpToTwoDecimalPlaces =
      Math.round(sequenceAdherenceOfWeek * 100) / 100;
    const weekString =
      moment(range[0]).format('DD') + '-' + moment(range[1]).format('DD MMM');
    weekWiseCountMap.set(
      weekString,
      weeklyPlannedCount <= 0
        ? null
        : sequenceAdherenceOfWeekRoundUpToTwoDecimalPlaces > 0
          ? sequenceAdherenceOfWeekRoundUpToTwoDecimalPlaces
          : 0,
    );
  });
  return weekWiseCountMap;
};

export const getWeekWiseSequenceAdherenceGraphData = (data, from, to) => {
  const weekDateRangeArray = getWeekRangeArrayInAMonth(from);
  const weekWiseSequenceAdherenceGraphDataMap = new Map();
  data.forEach(item => {
    weekWiseSequenceAdherenceGraphDataMap.set(
      item?.line?.label,
      getWeekWiseCountMap(item?.data, weekDateRangeArray),
    );
  });
  return buildD3SupportedMultiLineGraphData(
    weekWiseSequenceAdherenceGraphDataMap,
  );
};

const getMonthWiseCountMap = (data, monthDateRangeArray) => {
  let monthWiseCountMap = new Map();
  monthDateRangeArray.forEach(range => {
    const array = data.filter(
      record =>
        moment(record.date).isBetween(moment(range[0]), moment(range[1])) ||
        moment(record.date).isSame(moment(range[0])) ||
        moment(record.date).isSame(moment(range[1])),
    );
    const arrayOfMonthlyTotalPlannedCount = array.map(
      record => record.totalPlannedCount,
    );
    const arrayOfMonthlyLineOutCount = array.map(
      record => record.linedOutCount,
    );
    const monthlyPlannedCount = arrayOfMonthlyTotalPlannedCount.reduce(
      (a, b) => a + b,
      0,
    );
    const monthlyLineOutCount = arrayOfMonthlyLineOutCount.reduce(
      (a, b) => a + b,
      0,
    );
    const sequenceAdherenceOfWeek =
      ((monthlyPlannedCount - monthlyLineOutCount) / monthlyPlannedCount) *
      100 || 0;
    const sequenceAdherenceOfWeekRoundUpToTwoDecimalPlaces =
      Math.round(sequenceAdherenceOfWeek * 100) / 100;
    const weekString =
      moment(range[0]).format('DD') + '-' + moment(range[1]).format('DD MMM');
    monthWiseCountMap.set(
      weekString,
      monthlyPlannedCount <= 0
        ? null
        : sequenceAdherenceOfWeekRoundUpToTwoDecimalPlaces > 0
          ? sequenceAdherenceOfWeekRoundUpToTwoDecimalPlaces
          : 0,
    );
  });
  return monthWiseCountMap;
};

export const getMonthWiseSequenceAdherenceGraphData = (data, from, to) => {
  const monthDateRangeArray = getMonthRangeArrayforSixMonthSpan(from);
  const monthWiseSequenceAdherenceGraphDataMap = new Map();
  data.forEach(item => {
    monthWiseSequenceAdherenceGraphDataMap.set(
      item?.line?.label,
      getMonthWiseCountMap(item?.data, monthDateRangeArray),
    );
  });
  return buildD3SupportedMultiLineGraphData(
    monthWiseSequenceAdherenceGraphDataMap,
  );
};
const getWeekWiseGraphDataFor = (
  data,
  from,
  to,
  contributingFactor,
  factorTypes,
  timestampField,
) => {
  const topNRecordsInAscendingOrder = getTopNRecordsCountMap(
    data,
    contributingFactor,
    factorTypes,
  );

  const weekDateRangeArray = getWeekRangeArrayInAMonth(from);
  let reasonOrAgencyWithWeekWiseCountMap = new Map();

  topNRecordsInAscendingOrder.forEach((value, key) => {
    let weekWiseCountMap = new Map();
    for (let weekCounter = 0; weekCounter < noOfAWeeksInAMonth; weekCounter++) {
      const weekString =
        weekDateRangeArray[weekCounter][0].format('DD') +
        '-' +
        weekDateRangeArray[weekCounter][1].format('DD MMM');
      weekWiseCountMap.set(weekString, 0);
    }

    reasonOrAgencyWithWeekWiseCountMap.set(key, weekWiseCountMap);
  });

  let record;
  let reasonOrAgency;
  for (record of data) {
    reasonOrAgency = getTagValueForKey(record['tags'], contributingFactor);

    if (topNRecordsInAscendingOrder.has(reasonOrAgency)) {
      const timestamp = moment(record[timestampField]);
      const rangeForGivenTimestamp = getWeekRangeForGivenTimestamp(
        weekDateRangeArray,
        timestamp,
      );

      const previousCount = reasonOrAgencyWithWeekWiseCountMap
        .get(reasonOrAgency)
        .get(rangeForGivenTimestamp);

      const newCount = previousCount + parseInt(record['value']);
      reasonOrAgencyWithWeekWiseCountMap.set(
        reasonOrAgency,
        reasonOrAgencyWithWeekWiseCountMap
          .get(reasonOrAgency)
          .set(rangeForGivenTimestamp, newCount),
      );
    }
  }

  const maxRecordCount = calculateGraphMaximumRecordCount(
    reasonOrAgencyWithWeekWiseCountMap,
  );

  const graphDataArray = buildD3SupportedMultiLineGraphData(
    reasonOrAgencyWithWeekWiseCountMap,
  );

  const filteredGraphDataArray = getValidGraphData(graphDataArray);
  return [maxRecordCount, filteredGraphDataArray];
};

const getTransitionField = transition => {
  let transitionField;
  switch (transition) {
    case 'Planned':
      transitionField = 'plannedAsnCount';
      break;
    case 'LineOn':
      transitionField = 'linedOnAsnCount';
      break;
    case 'LineOut':
      transitionField = 'linedOutAsnCount';
      break;
    case 'Dropped':
      transitionField = 'droppedAsnCount';
      break;
    case 'RollOut':
      transitionField = 'rolledOutAsnCount';
      break;
    case 'WTCL':
      transitionField = 'wtclAsnCount';
      break;
    case 'SFLT':
      transitionField = 'sfltAsnCount';
      break;
    default:
      transitionField = null;
  }
  return transitionField;
};
export const getDayWiseAsnTransitionGraphData = (
  asnTransitionRawData,
  from,
) => {
  let lineOutDayWisePercentageDataMap = new Map();
  for (let iterator = 0; iterator < noOfDaysInAWeek; iterator++) {
    let countByTransition = new Map();

    asnTransitions.forEach(transition => {
      countByTransition.set(transition, 0);
    });

    let dateString = moment(from).add(iterator, 'd').format('DD MMM');
    lineOutDayWisePercentageDataMap.set(dateString, countByTransition);
  }

  let record;
  for (record of asnTransitionRawData) {
    const timestamp = moment(record['date']).format('DD MMM');
    for (let transition of asnTransitions) {
      const transitionCount = record[getTransitionField(transition)];
      lineOutDayWisePercentageDataMap.set(
        timestamp,
        lineOutDayWisePercentageDataMap
          .get(timestamp)
          .set(
            transition,
            lineOutDayWisePercentageDataMap.get(timestamp).get(transition) +
            transitionCount,
          ),
      );
    }
  }

  const maxRecordCount = calculateGraphMaximumRecordCount(
    lineOutDayWisePercentageDataMap,
  );

  const graphDataArray = buildD3SupportedMultiBarGraphData(
    lineOutDayWisePercentageDataMap,
  );

  return [maxRecordCount, graphDataArray];
};

export const getWeekWiseAsnTransitionGraphData = (
  asnTransitionRawData,
  from,
) => {
  const weekDateRangeArray = getWeekRangeArrayInAMonth(from);

  let lineOutWeekWisePercentageDataMap = new Map();

  for (let weekCounter = 0; weekCounter < noOfAWeeksInAMonth; weekCounter++) {
    let countByTransition = new Map();

    asnTransitions.forEach(transition => {
      countByTransition.set(transition, 0);
    });

    const weekString =
      weekDateRangeArray[weekCounter][0].format('DD') +
      '-' +
      weekDateRangeArray[weekCounter][1].format('DD MMM');

    lineOutWeekWisePercentageDataMap.set(weekString, countByTransition);
  }

  let record;
  for (record of asnTransitionRawData) {
    const timestamp = moment(record['date']);
    const rangeForGivenTimestamp = getWeekRangeForGivenTimestamp(
      weekDateRangeArray,
      timestamp,
    );

    for (let transition of asnTransitions) {
      const transitionCount = record[getTransitionField(transition)];
      lineOutWeekWisePercentageDataMap.set(
        rangeForGivenTimestamp,
        lineOutWeekWisePercentageDataMap
          .get(rangeForGivenTimestamp)
          .set(
            transition,
            lineOutWeekWisePercentageDataMap
              .get(rangeForGivenTimestamp)
              .get(transition) + transitionCount,
          ),
      );
    }
  }

  const maxRecordCount = calculateGraphMaximumRecordCount(
    lineOutWeekWisePercentageDataMap,
  );

  const graphDataArray = buildD3SupportedMultiBarGraphData(
    lineOutWeekWisePercentageDataMap,
  );

  return [maxRecordCount, graphDataArray];
};

const getWeekRangeArrayInAMonth = (from, to) => {
  let weekDateRangeArray = [];
  for (let weekCounter = 0; weekCounter < noOfAWeeksInAMonth; weekCounter++) {
    const startDate = moment(from).add(weekCounter * noOfDaysInAWeek, 'd');
    const endDate = moment(from)
      .add(weekCounter * noOfDaysInAWeek, 'd')
      .add(6, 'd');
    weekDateRangeArray[weekCounter] = [];
    weekDateRangeArray[weekCounter][0] = startDate;
    weekDateRangeArray[weekCounter][1] = endDate;
  }
  return weekDateRangeArray;
};
const getMonthRangeArrayforSixMonthSpan = (from, to) => {
  let monthRangeArray = [];
  for (
    let monthCounter = 0;
    monthCounter < noOfMonthsInASixMonthSpan;
    monthCounter++
  ) {
    const startDate = moment(from)
      .add(monthCounter, 'M')
      .startOf('month')
      .format('YYYY-MM-DD hh:mm');
    const endDate = moment(from)
      .add(monthCounter, 'M')
      .endOf('month')
      .format('YYYY-MM-DD hh:mm');
    monthRangeArray[monthCounter] = [];
    monthRangeArray[monthCounter][0] = moment(startDate);
    monthRangeArray[monthCounter][1] = moment(endDate);
  }
  return monthRangeArray;
};
const calculateGraphMaximumRecordCount = graphDataMap => {
  let maxRecordCount = 0;

  for (let dataRecord of graphDataMap.values()) {
    const values = Array.from(dataRecord.values());
    const validValues = values.filter(value => isFinite(value));
    let currentMaximumValue = Math.max(...validValues);
    if (maxRecordCount < currentMaximumValue) {
      maxRecordCount = currentMaximumValue;
    }
  }

  maxRecordCount *= 1.25;
  maxRecordCount = maxRecordCount.toFixed(0);

  const parsedMaxRecordCount = parseInt(maxRecordCount);
  if (parsedMaxRecordCount === 0 || parsedMaxRecordCount < 10) {
    maxRecordCount = 10;
  }
  return maxRecordCount;
};

const getValidGraphData = graphDataArray => {
  return graphDataArray.map(record => {
    let validRecord = {};
    validRecord = { id: record.id };

    let coordinateDataArray = record['data'];
    let dataArrayWithReplacingInvalidRecordsWithNull = coordinateDataArray.map(
      graphData => {
        if (!graphData.x || isNaN(graphData.y) || !isFinite(graphData.y)) {
          return null;
        } else {
          return { x: graphData.x, y: graphData.y };
        }
      },
    );
    let validCoordinateDataArray = dataArrayWithReplacingInvalidRecordsWithNull.filter(
      element => element,
    );
    validRecord['data'] = validCoordinateDataArray;
    return validRecord;
  });
};

const getWeekRangeForGivenTimestamp = (weekDateRangeArray, timestamp) => {
  const givenDate = timestamp;

  for (let weekCounter = 0; weekCounter < noOfAWeeksInAMonth; weekCounter++) {
    const weekStartDate = weekDateRangeArray[weekCounter][0];
    const weekEndDate = weekDateRangeArray[weekCounter][1];

    if (givenDate >= weekStartDate && givenDate <= weekEndDate) {
      const weekString =
        weekDateRangeArray[weekCounter][0].format('DD') +
        '-' +
        weekDateRangeArray[weekCounter][1].format('DD MMM');
      return weekString;
    }
  }
};

const getTopNRecordsCountMap = (data, contributingFactor, factorTypes) => {
  let reasonOrAgency;
  let reasonOrAgencyCountMap = new Map();
  for (reasonOrAgency of factorTypes) {
    reasonOrAgencyCountMap.set(reasonOrAgency, 0);
  }

  let record;
  for (record of data) {
    reasonOrAgency = getTagValueForKey(record['tags'], contributingFactor);
    const newCount =
      reasonOrAgencyCountMap.get(reasonOrAgency) + record['value'];
    reasonOrAgencyCountMap.set(reasonOrAgency, newCount);
  }

  const countMapInDescendingOrder = new Map(
    [...reasonOrAgencyCountMap].sort(
      (a, b) => (a[1] > b[1] && -1) || (a[1] === b[1] ? 0 : 1), // sort in descending order
    ),
  );

  let topNRecords = new Map();

  const iterator = countMapInDescendingOrder.entries();
  for (let counter = 1; counter <= topNRecordsCount; counter++) {
    let currentRecord = iterator.next();
    const key = currentRecord.value[0];
    const value = currentRecord.value[1];
    topNRecords.set(key, value);
  }

  const topNRecordsInAscendingOrder = new Map(
    [...topNRecords].sort(
      (a, b) => (a[1] > b[1] && 1) || (a[1] === b[1] ? 0 : -1), // sort in ascending order
    ),
  );

  return topNRecordsInAscendingOrder;
};

const getTagValueForKey = (keyValueArray, key) => {
  let currentKeyValuePair;
  for (currentKeyValuePair of keyValueArray) {
    if (currentKeyValuePair['key'] === key) {
      return currentKeyValuePair['value'];
    }
  }
};

const buildD3SupportedMultiLineGraphData = reasonOrAgencyWithDateWiseCountMap => {
  let dataArray = [];
  reasonOrAgencyWithDateWiseCountMap.forEach((value, key) => {
    let jsonData = {};
    jsonData['id'] = key;
    let dateWiseRecordMap = reasonOrAgencyWithDateWiseCountMap.get(key);

    jsonData['data'] = [...dateWiseRecordMap].map(([x, y]) => ({
      x,
      y,
    }));

    dataArray.push(jsonData);
  });
  return dataArray;
};

const buildD3SupportedMultiBarGraphData = reasonOrAgencyWithDateWiseCountMap => {
  let dataArray = [];
  reasonOrAgencyWithDateWiseCountMap.forEach((value, key) => {
    let jsonData = {};
    jsonData['id'] = key;
    let transitionCountMap = reasonOrAgencyWithDateWiseCountMap.get(key);
    transitionCountMap.forEach((value, key) => {
      jsonData[key] = value;
    });
    dataArray.push(jsonData);
  });
  return dataArray;
};

export const calculateDateRangeForCurrentDurationForWeekWiseGraph = () => {
  const startDateTime = moment()
    .utc()
    .startOf('week')
    .add(6, 'd')
    .subtract(27, 'd')
    .format(DATE_FORMAT.ISO);
  const endDateTime = moment()
    .utc()
    .startOf('week')
    .add(6, 'd')
    .format(DATE_FORMAT.ISO);
  return [startDateTime, endDateTime];
};
export const calculateDateRangeForPreviousDurationForWeekWiseGraph = () => {
  const startDateTime = moment()
    .utc()
    .startOf('week')
    .add(6, 'd')
    .subtract(27, 'd')
    .subtract(28, 'd')
    .format(DATE_FORMAT.ISO);
  const endDateTime = moment()
    .utc()
    .startOf('week')
    .add(6, 'd')
    .subtract(28, 'd')
    .format(DATE_FORMAT.ISO);
  return [startDateTime, endDateTime];
};
export const calculateDateRangeForCurrentDurationForMonthWiseGraph = () => {
  const startDateTime = moment()
    .utc()
    .startOf('month')
    .add(30, 'd')
    .subtract(180, 'd')
    .format(DATE_FORMAT.ISO);

  const endDateTime = moment()
    .utc()
    .startOf('month')
    .add(30, 'd')
    .format(DATE_FORMAT.ISO);
  return [startDateTime, endDateTime];
};
export const calculateDateRangeForPreviousDurationForMonthWiseGraph = () => {
  const startDateTime = moment()
    .utc()
    .startOf('month')
    .add(30, 'd')
    .subtract(179, 'd')
    .subtract(180, 'd')
    .format(DATE_FORMAT.ISO);
  const endDateTime = moment()
    .utc()
    .startOf('month')
    .add(30, 'd')
    .subtract(180, 'd')
    .format(DATE_FORMAT.ISO);

  return [startDateTime, endDateTime];
};

export const calculateStartDateTimeWithZoneInfo = (daysToBeSubtracted = 0) => {
  return moment()
    .utc()
    .subtract(daysToBeSubtracted, 'd')
    .format(DATE_FORMAT.ISO);
};

export const calculateEndDateTimeWithZoneInfo = (daysToBeSubtracted = 0) => {
  return moment()
    .utc()
    .subtract(daysToBeSubtracted, 'd')
    .format(DATE_FORMAT.ISO);
};
